import { Suspense, type ReactElement } from 'react';
import { Await } from 'react-router';
import EatalyBreadcrumbs, { EATALY_BREADCRUMB_DEFAULT_BACKGROUND_CLASS } from './index';
import { EatalyBreadcrumbsPendingFallback } from './skeleton';
import { type EatalyBreadcrumbLoaderResult, type EatalyBreadcrumbResolvedInput, isThenable } from './types';

export type { EatalyBreadcrumbLoaderResult, EatalyBreadcrumbResolvedInput } from './types';

export type EatalyBreadcrumbsResolverProps = {
    /**
     * Trail from the **leaf** route `loader` (see `useEatalyBreadcrumbs`).
     * Sync on PLP/content; a Promise on PDP when product/category data is deferred.
     */
    items: EatalyBreadcrumbResolvedInput;
    /**
     * Last resolved sync trail (e.g. PLP). Used in the deferred-PDP `Suspense` fallback: real segments plus a
     * product-name skeleton, or omitted on cold PDP load (full strip skeleton instead).
     */
    staleItemsWhilePending?: EatalyBreadcrumbLoaderResult['items'] | undefined;
    /**
     * Nav strip background from the leaf route loader (`breadcrumbBackgroundClass`), forwarded so fallback + resolved
     * states match the current page.
     */
    backgroundClassName?: string;
    /** Custom destination for the home icon link. Omitted when the route has no shopping pillar context. */
    homeTo?: string;
    /** Whether to show the home icon link. Defaults to true. */
    showHome?: boolean;
};

function renderBreadcrumbs(
    { items, mergeHomeWithFirstItem }: EatalyBreadcrumbLoaderResult,
    props: Omit<EatalyBreadcrumbsResolverProps, 'items' | 'staleItemsWhilePending'>
): ReactElement | null {
    if (items.length === 0) {
        return null;
    }

    return (
        <EatalyBreadcrumbs
            backgroundClassName={props.backgroundClassName ?? EATALY_BREADCRUMB_DEFAULT_BACKGROUND_CLASS}
            items={items}
            homeTo={props.homeTo}
            showHome={props.showHome}
            mergeHomeWithFirstItem={mergeHomeWithFirstItem}
        />
    );
}

/**
 * Resolves `items` from route loaders, then renders `EatalyBreadcrumbs`.
 * Renders **nothing** when the trail is `undefined` or empty.
 * Uses React Router `Await` + `Suspense` for the deferred PDP Promise.
 */
export default function EatalyBreadcrumbsResolver({
    items,
    staleItemsWhilePending,
    backgroundClassName = EATALY_BREADCRUMB_DEFAULT_BACKGROUND_CLASS,
    homeTo,
    showHome,
}: EatalyBreadcrumbsResolverProps): ReactElement | null {
    const sharedProps = { backgroundClassName, homeTo, showHome };
    const deferredPendingFallback = (
        <EatalyBreadcrumbsPendingFallback
            staleItemsWhilePending={staleItemsWhilePending}
            backgroundClassName={backgroundClassName}
        />
    );

    if (items == null) {
        if (staleItemsWhilePending != null && staleItemsWhilePending.length > 0) {
            return renderBreadcrumbs({ items: staleItemsWhilePending, mergeHomeWithFirstItem: false }, sharedProps);
        }
        return null;
    }

    if (isThenable<EatalyBreadcrumbLoaderResult>(items)) {
        return (
            <Suspense fallback={deferredPendingFallback}>
                <Await resolve={items} errorElement={null}>
                    {(resolved: EatalyBreadcrumbLoaderResult | null | undefined) => {
                        if (resolved == null) {
                            return null;
                        }
                        return renderBreadcrumbs(resolved, sharedProps);
                    }}
                </Await>
            </Suspense>
        );
    }

    return renderBreadcrumbs(items, sharedProps);
}
